import {get} from 'object-path-immutable'
import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {Helmet} from 'react-helmet-async'
import {CredentialRoles} from '../../../common/types'
import {ButtonGroup, FormRow} from '../../../components/form'
import {Button} from '../../../components/library/buttons/button'
import {CopyInput, Copyable} from '../../../components/library/copy'
import {
  DropdownBody,
  DropdownFooter,
  DropdownItem
} from '../../../components/library/dropdown/content'
import {Select, TextInput} from '../../../components/library/form'
import {Icon} from '../../../components/library/icon/icon'
import {
  List,
  ListColumns,
  NoResultsPermissions
} from '../../../components/library/list'
import {Modal} from '../../../components/library/modal/modal'
import {Tag} from '../../../components/library/tag/tag'
import {OverflowFooter} from '../../../components/overflow'
import {
  CustomerTokenDetailFragment,
  useCustomerTokenListLazyQuery,
  useDeleteCustomerTokenMutation,
  useUpdateCustomerTokenMutation
} from '../../../data/workbench-api/generated/customer-token'
import {
  PermissionAction,
  PermissionResource
} from '../../../data/workbench-api/generated/types'
import {EnvironmentNode} from '../../../environment'
import {useAnalytics} from '../../../lib/analytics/analytics'
import {getToken} from '../../../lib/api'
import {notify} from '../../../lib/bugsnag'
import {getApi} from '../../../lib/fetch'
import {toTitleCase} from '../../../lib/helpers'
import {Error} from '../../action-flow/components/error'
import {
  DeleteLink,
  IconHeading,
  Mono,
  Section,
  SectionContent,
  SectionInfo
} from '../components'
import styled from 'styled-components'
import {ENVIRONMENT_NAME} from '../../../lib/env'

interface CredentialsProps {
  environment: EnvironmentNode
}

interface ModalCreate {
  open: boolean
  role?: CredentialRoles
  name?: string
  message?: string
  error?: string
}

interface ModalEdit {
  open: boolean
  id: string
  role: string
  name: string
}

const Info = styled.p`
  display: flex;
  gap: 10px;
  align-items: center;
  margin: 0 10px;
`

const ListOverflow = ({
  customerToken,
  closeDropdown,
  refetch,
  edit,
  canEdit
}: {
  customerToken: CustomerTokenDetailFragment
  closeDropdown: () => void
  refetch: () => void
  edit: () => void
  canEdit: boolean
}) => {
  const [archiveMutation] = useUpdateCustomerTokenMutation()
  const [deleteMutation] = useDeleteCustomerTokenMutation()

  return (
    <>
      <DropdownBody>
        <DropdownItem disabled={!canEdit}>
          <a
            onClick={() => {
              if (canEdit) {
                edit()
                closeDropdown()
              }
            }}
          >
            Edit
          </a>
          <a
            onClick={() => {
              if (canEdit) {
                archiveMutation({
                  variables: {
                    id: customerToken.id,
                    environmentId: customerToken.environmentId,
                    enabled: !customerToken.enabled
                  }
                }).then(closeDropdown)
              }
            }}
          >
            {customerToken.enabled ? 'Disable' : 'Enable'}
          </a>
        </DropdownItem>
        {!customerToken.enabled ? (
          <DropdownItem disabled={!canEdit}>
            <DeleteLink
              onClick={() => {
                if (
                  canEdit &&
                  confirm('Deletion is permanent - are you sure?')
                ) {
                  deleteMutation({
                    variables: {
                      id: customerToken.id,
                      environmentId: customerToken.environmentId
                    }
                  }).then(() => {
                    refetch()
                    closeDropdown()
                  })
                }
              }}
            >
              Delete
            </DeleteLink>
          </DropdownItem>
        ) : null}
      </DropdownBody>
      <DropdownFooter>
        <OverflowFooter
          id={customerToken.id}
          created={customerToken.created}
          updated={customerToken.updated}
        />
      </DropdownFooter>
    </>
  )
}

const roles: CredentialRoles[] = ['auth', 'workbench', 'events']

const createModalDefaultState: ModalCreate = {
  open: false,
  role: 'events',
  name: ''
}

const getOauthUrl = () => {
  return `https://${
    ENVIRONMENT_NAME === 'staging'
      ? 'master' // staging and master use same oauth
      : ENVIRONMENT_NAME === 'local'
      ? 'development' // local uses development oauth
      : ENVIRONMENT_NAME
  }-atomic-io.auth.us-east-1.amazoncognito.com/oauth2/token`
}

export const AuthenticationControls = ({environment}: CredentialsProps) => {
  const canEdit = environment.hasPermission(
    PermissionResource.CustomerToken,
    PermissionAction.Edit
  )
  const canView = environment.hasPermission(
    PermissionResource.CustomerToken,
    PermissionAction.View
  )
  const [working, setWorking] = useState<boolean>(false)
  const [modalClientSecret, setModalClientSecret] = useState<string>()
  const [modalCreate, setModalCreate] = useState<ModalCreate>(
    createModalDefaultState
  )
  const [modalEdit, setModalEdit] = useState<ModalEdit>({
    open: false,
    id: '',
    role: '',
    name: ''
  })

  const [query, {data, refetch = () => null, loading}] =
    useCustomerTokenListLazyQuery({
      variables: {
        environmentId: environment.id
      }
    })

  useEffect(() => {
    if (canView) {
      query()
    }
  }, [canView, query])

  const rows = data?.customerTokens?.nodes
  const api = getApi({organisationId: environment.organisationId, getToken})

  const create = useCallback(async () => {
    setWorking(true)
    const res = await api(
      'post',
      `/workbench/v1/${environment.id}/credentials`,
      {
        role: modalCreate.role,
        name: modalCreate.name
      }
    )
    setWorking(false)
    const secret: false | string | undefined =
      res.ok && get(res, 'json.data.clientSecret')

    if (!secret) {
      setModalCreate(m => ({
        ...m,
        error:
          'Sorry something went wrong while creating the credential. Please try again.'
      }))
      notify('No secret returned from credentials API', {
        environmentId: environment.id,
        organisationId: environment.organisationId
      })
    } else {
      setModalCreate(createModalDefaultState)
      setModalClientSecret(secret)
      refetch()
    }
  }, [environment, refetch, modalCreate.role, modalCreate.name, api])

  const [updateMutation] = useUpdateCustomerTokenMutation()

  const columns: ListColumns<CustomerTokenDetailFragment> = useMemo(
    () => [
      {
        id: 'name',
        header: 'Name',
        accessor: r => {
          const name = r.name || 'Unnamed'
          return (
            <>
              {canEdit ? (
                <a
                  onClick={() =>
                    setModalEdit(m => ({
                      ...m,
                      open: true,
                      id: r.id,
                      name: r.name
                    }))
                  }
                >
                  {name}
                </a>
              ) : (
                <strong style={{marginRight: '10px'}}>{name}</strong>
              )}
            </>
          )
        }
      },
      {
        id: 'client_id',
        header: 'Client ID',
        accessor: r => (
          <>
            {r.enabled ? (
              <Tag appearance="green">Active</Tag>
            ) : (
              <Tag appearance="light">Inactive</Tag>
            )}
            <Mono style={{marginRight: '5px', marginLeft: '10px'}}>
              {r.clientId}
            </Mono>
            <Copyable style={{width: '70px'}} value={r.clientId} />
          </>
        )
      },
      {
        id: 'role',
        header: 'Role',
        accessor: r => toTitleCase(r.role)
      }
    ],
    [canEdit]
  )

  const analytics = useAnalytics()

  useEffect(() => {
    analytics.track({
      event: 'Viewed api keys configuration',
      properties: {
        org_id: environment.organisationId,
        environment_id: environment.id,
        category: 'configuration>api>authentication-controls'
      }
    })
  }, [analytics, environment])

  return (
    <Section>
      <Helmet>
        <title>API keys</title>
      </Helmet>
      <header>
        <IconHeading>
          <Icon name="padlock" /> API keys
        </IconHeading>
        <nav>
          <Button
            disabled={!canEdit}
            onClick={() => setModalCreate(m => ({...m, open: true}))}
          >
            New credential
          </Button>
        </nav>
      </header>

      <SectionInfo>
        <p>Used to authenticate your requests to the Atomic API</p>
      </SectionInfo>

      <Section>
        <SectionInfo>
          <p>The OAuth URL for retrieving an access token</p>
        </SectionInfo>
        <CopyInput size="large" value={getOauthUrl()} />
      </Section>

      <SectionContent>
        <List<CustomerTokenDetailFragment>
          columns={columns}
          rows={rows}
          loading={loading}
          bleed={'#f2f2f2'}
          renderOverflow={(customerToken, closeDropdown) => (
            <ListOverflow
              customerToken={customerToken}
              closeDropdown={closeDropdown}
              refetch={refetch}
              edit={() => {
                if (canEdit) {
                  setModalEdit({
                    open: true,
                    id: customerToken.id,
                    name: customerToken.name,
                    role: customerToken.role
                  })
                }
              }}
              canEdit={canEdit}
            />
          )}
          renderEmpty={canView ? undefined : () => <NoResultsPermissions />}
        />
      </SectionContent>
      {!!modalClientSecret && (
        <Modal
          title="Client secret"
          handleClose={() => setModalClientSecret(undefined)}
        >
          <Info>
            <Icon name="info" />
            <p>
              This secret will not be displayed again. Please save it before
              closing this modal.
            </p>
          </Info>
          <CopyInput value={modalClientSecret} />
          <ButtonGroup>
            <Button
              size="large"
              onClick={() => setModalClientSecret(undefined)}
            >
              Close
            </Button>
          </ButtonGroup>
        </Modal>
      )}
      {modalCreate.open && (
        <Modal
          title="Create new"
          handleClose={() => setModalCreate({open: false, role: undefined})}
        >
          <form
            onSubmit={e => {
              e.preventDefault()
              create()
            }}
          >
            <Select
              label="Role"
              options={roles.map(role => ({
                value: role,
                text: toTitleCase(role)
              }))}
              value={modalCreate.role}
              onChange={role =>
                setModalCreate(m => ({...m, role: role as CredentialRoles}))
              }
            />

            <>
              <FormRow>
                <TextInput
                  upLabel="Name"
                  label="Give it a name..."
                  value={modalCreate.name}
                  onChange={name => setModalCreate(m => ({...m, name: name}))}
                />
              </FormRow>
            </>

            {modalCreate.error && <Error>{modalCreate.error}</Error>}

            <ButtonGroup>
              <Button type="submit" disabled={working} size="large">
                Add
              </Button>
            </ButtonGroup>
          </form>
        </Modal>
      )}
      {modalEdit.open && (
        <Modal
          title="Edit name"
          handleClose={() => setModalEdit(m => ({...m, open: false, name: ''}))}
        >
          <FormRow>
            <TextInput
              upLabel="Name"
              label="Give it a name..."
              value={modalEdit.name}
              onChange={name => setModalEdit(m => ({...m, name: name}))}
            />
          </FormRow>

          <Info>
            <Icon name="info" />
            <p>Secrets are only displayed on credential creation.</p>
          </Info>

          <ButtonGroup>
            <Button
              type="submit"
              onClick={() => {
                updateMutation({
                  variables: {
                    environmentId: environment.id,
                    id: modalEdit.id,
                    name: modalEdit.name
                  }
                }).then(() =>
                  setModalEdit(m => ({...m, open: false, name: ''}))
                )
              }}
              disabled={working}
              size="large"
            >
              Save
            </Button>
          </ButtonGroup>
        </Modal>
      )}
    </Section>
  )
}
